import Upload                         from 'antd/lib/upload';
import { InboxOutlined }              from '@ant-design/icons';
import { CloseOutlined }              from '@ant-design/icons';
import React                          from 'react';
import { ReactElement }               from 'react';
import View                           from '@widesk-ui/components/View';
import useTheme                       from '@widesk-ui/hooks/useTheme';
import ImgCrop                        from 'antd-img-crop';
import { Modal }                      from '@widesk-ui/components/modal/Modal';
import Image                          from '@widesk-ui/components/Image';
import { RcFile }                     from 'antd/es/upload';
import { observer }                   from 'mobx-react-lite';
import { message }                    from '@widesk-ui/hooks/useMessage';
import { UploadRef }                  from 'antd/lib/upload/Upload';
import { convertAspectStringToFloat } from '@widesk-ui/helpers/FileImageHelper';
import { getFileDimension }           from '@widesk-ui/helpers/FileImageHelper';
import { toBase64 }                   from '@widesk-ui/helpers/FileImageHelper';

export type FileUploadValue = { fileList: File[] };

export type FileUploadProps = Omit<FieldProps, 'value'> & {
	hideRequirements?: boolean;
	imageRequirements?: ImageRequirements; // Aspect et dimensions requises de l'image sélectionnée.
	mimeTypeReferences?: string[]; // Les types mimes acceptés par l'input (sinon rejeté)
	multiple?: boolean; // Si true, l'input acceptera plusieurs fichiers
	onFileListChange?: (fl: File[]) => void; // Callback onChange de la liste de fichiers de l'input
	showImgEditor?: boolean; // Affiche l'éditeur d'image (si le fichier sélectionné est une image)
	value?: FileUploadValue // Permet le bon fonctionnement avec un FormItem
	forceSvgToHaveWidthAndHeight?: boolean;
	children?: React.ReactNode;
};

export default function FileUpload(props: FileUploadProps) {
	const theme = useTheme();

	const aspect = props.imageRequirements?.aspect;
	const minDimensions = props.imageRequirements?.minDimensions;

	const uploadDraggerRef = React.useRef<UploadRef>(null);

	const setFileList = (fileList: File[]) => {
		if (props.onChange) {
			props.onChange(fileList.length ? {
				fileList: fileList,
			} : undefined);
		}
	};

	React.useEffect(() => {
		const fileList = props.value?.fileList;
		if (fileList && (
			(fileList.length > 1 && !props.multiple)
			|| !fileList.some(f => props.mimeTypeReferences?.includes(f.type))
		)) {
			setFileList([]);
		}
	}, [props.multiple, props.mimeTypeReferences]);

	return (
		<>
			{(props.mimeTypeReferences && !props.hideRequirements) && (
				<View size={theme.sizeSM} color={theme.colorTextDescription} marginB={theme.marginXXS}>
					<span>Extensions : {props.mimeTypeReferences.join(', ')}</span>
					{aspect && (
						<span>Format d'image : {aspect}</span>
					)}
					{minDimensions && (
						<span>Dimensions minimales : {minDimensions.width}x{minDimensions.height}</span>
					)}
					<span>Quantité : {props.multiple ? 'plusieurs fichiers' : 'un seul fichier'}</span>
				</View>
			)}

			<View heightF style={{ ...props.style, display: 'block' }}>
				<ConditionalWrapper imageRequirements={props.imageRequirements} showImgEditor={!!props.showImgEditor}>
					<Upload.Dragger
						ref={uploadDraggerRef}
						accept={props.mimeTypeReferences?.join(',')}
						beforeUpload={async currentFile => {
							const mimeTypeCheck = !props.mimeTypeReferences
								|| !props.mimeTypeReferences?.length
								|| props.mimeTypeReferences.includes(currentFile.type);

							let minDimensionsCheck = true;
							if (minDimensions) {
								const fileDimensions = await getFileDimension(currentFile);
								if (fileDimensions.height < minDimensions.height || fileDimensions.width < minDimensions.width) {
									minDimensionsCheck = false;
								}
							}

							if (mimeTypeCheck && minDimensionsCheck) {
								let fileToUpload = currentFile;

								if (props.forceSvgToHaveWidthAndHeight && currentFile.type === 'image/svg+xml') {
									const svgContent = await new Promise<string>((resolve, reject) => {
										const reader = new FileReader();
										reader.onload = (e) => {
											const content = e.target?.result;
											if (typeof content === 'string') resolve(content);
											else reject('Impossible de lire le fichier');
										};
										reader.onerror = () => reject(reader.error);
										reader.readAsText(currentFile, 'UTF-8');
									});

									// Fonction pour modifier le SVG
									const modifySVG = (svg: string) => {
										const parser = new DOMParser();
										const serializer = new XMLSerializer();
										const doc = parser.parseFromString(svg, 'image/svg+xml');
										const svgEl = doc.getElementsByTagName('svg')[0];

										if (!svgEl) return svg;

										// Ajouter version="1.1" si absent
										if (!svgEl.hasAttribute('version')) {
											svgEl.setAttribute('version', '1.1');
										}

										// Forcer width et height basés sur viewBox
										const viewBox = svgEl.getAttribute('viewBox');
										if (viewBox) {
											const parts = viewBox.split(/\s+/);
											if (parts.length === 4) {
												const vbWidth = parts[2] ?? '100';
												const vbHeight = parts[3] ?? '100';
												svgEl.setAttribute('width', vbWidth);
												svgEl.setAttribute('height', vbHeight);
											}
										}

										return serializer.serializeToString(doc);
									};

									const modifiedSVG = modifySVG(svgContent);

									// Créer un nouveau fichier à uploader
									// eslint-disable-next-line @typescript-eslint/ban-ts-comment
									// @ts-ignore
									fileToUpload = new File([modifiedSVG], currentFile.name, { type: 'image/svg+xml' });
								}

								const newFileList = props.multiple
									? [...props.value?.fileList || [], fileToUpload]
									: [fileToUpload];

								setFileList(newFileList);

								if (props.onFileListChange) {
									props.onFileListChange(newFileList);
								}
							}

							return (mimeTypeCheck && minDimensionsCheck) ? false : Upload.LIST_IGNORE;
						}}

						fileList={props.value?.fileList as any || []}
						onPreview={async file => {
							file.preview = await toBase64(file);

							return Modal.open({
								title: file.name,
								children: (
									<View center>
										<Image preview={false} src={file.preview} />
									</View>
								),
								footer: null,
							});
						}}
						onRemove={file => {
							const index = props.value?.fileList.indexOf(file as RcFile);
							const newFileList = props.value?.fileList.slice();

							if (newFileList && index !== undefined) {
								newFileList.splice(index, 1);

								setFileList(newFileList);

								if (props.onFileListChange) {
									props.onFileListChange(newFileList);
								}
							}
						}}
						maxCount={!props.multiple ? 1 : undefined}
						style={{ overflow: 'hidden' }}
					>
						<>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>

							<p className="ant-upload-text" style={{ fontSize: 12 }}>
								Cliquez ou faites glisser le fichier dans cette zone
								pour télécharger
							</p>

							{props.children}
						</>
					</Upload.Dragger>
				</ConditionalWrapper>
			</View>
		</>
	);
}

/*
Wrap l'input Upload dans un ImgCrop si props.showImgEditor est true.
Permet d'ouvrir une modale de modification d'image (si le fichier est de type image).
 */
const ConditionalWrapper = observer((props: {
	imageRequirements?: ImageRequirements,
	showImgEditor: boolean,
	children: ReactElement
}) => {
	const theme = useTheme();

	const aspect = props.imageRequirements?.aspect ? convertAspectStringToFloat(props.imageRequirements?.aspect) : undefined;
	const minDimensions = props.imageRequirements?.minDimensions;
	const [maxZoom, setMaxZoom] = React.useState<number>();
	const [minZoom, setMinZoom] = React.useState<number>(1);

	return props.showImgEditor ? (
		<ImgCrop
			aspect={aspect}
			beforeCrop={async file => {
				const isImageCheck = file.type.startsWith('image');

				let minDimensionsCheck = true;
				const fileDimensions = await getFileDimension(file);

				// Calcule le minZoom pour que l'image entière soit visible dans le crop zone
				const imageAspect = fileDimensions.width / fileDimensions.height;
				const cropAspect = aspect ?? 1;
				setMinZoom(Math.min(imageAspect, cropAspect) / Math.max(imageAspect, cropAspect));

				if (minDimensions) {
					if (fileDimensions.height < minDimensions.height || fileDimensions.width < minDimensions.width) {
						minDimensionsCheck = false;
					} else {
						const maxZoomWidth = fileDimensions.width / minDimensions.width;
						const maxZoomHeight = fileDimensions.height / minDimensions.height;
						setMaxZoom(maxZoomWidth < maxZoomHeight ? maxZoomWidth : maxZoomHeight);
					}

					if (!minDimensionsCheck) {
						message.error(`Les dimensions minimales (${minDimensions.width}x${minDimensions.height} px) ne sont pas respectées.`);
					}
				}

				return minDimensionsCheck && isImageCheck;
			}}
			maxZoom={maxZoom}
			minZoom={minZoom}
			modalClassName="widesk-modal"
			modalProps={{
				closeIcon: <CloseOutlined style={{ color: theme.colorIcon }} />,
				styles: {
					content: { backgroundColor: theme.colorBgElevated },
					header: { backgroundColor: theme.colorBgElevated, color: theme.colorText },
					footer: { backgroundColor: theme.colorBgElevated },
					body: {
						paddingTop: theme.marginMD,
						paddingBottom: theme.marginMD,
						borderTop: `1px solid ${theme.colorBorder}`,
						borderBottom: `1px solid ${theme.colorBorder}`,
						color: theme.colorText,
					},
				},
				cancelButtonProps: { size: 'large' },
				okButtonProps: { size: 'large' },
			}}
			cropperProps={{
				style           : { containerStyle: { background: theme.colorBgLayout } },
				restrictPosition: minZoom >= 1,
				zoomSpeed       : 1,
				mediaProps      : {},
				// react-easy-crop >= 5.5 exige les props cropperProps et keyboardStep (comme mediaProps
				// ci-dessus) ; keyboardStep n'est requis que si antd-img-crop <= 4.17 ne l'omet pas de son type.
				// Elles sont passées via un spread : les projets sur une version antérieure, où ces clés
				// n'existent pas, ne déclenchent ainsi pas l'erreur "excess property" de TypeScript.
				// Les valeurs reprennent les défauts de react-easy-crop (aucun changement de comportement).
				...{ cropperProps: {}, keyboardStep: 1 },
			}}
			fillColor="transparent"
			modalTitle="Modification de l'image"
			modalWidth={750}
			quality={1}
			resetText="Réinitialiser"
			rotationSlider
			showGrid
			showReset
			zoomSlider
		>
			{props.children}
		</ImgCrop>
	) : props.children;
});